import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  getRoles,
  createRole,
  updateRole,
  deleteRole,
  subscribeToRoles,
  checkPermission,
  getRoleById
} from '../../../firebase/services/roleService';
import {
  getAllPermissions,
  getPermissionsByCategory,
  getAllCategories
} from '../../../firebase/services/permissionService';
import { useAuth } from '../../../contexts/AuthContext';
import { Role, PermissionId } from '../../../types/rbac';

const RoleManagementPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [newRole, setNewRole] = useState<Partial<Role>>({
    name: '',
    description: '',
    grantedPermissions: []
  });
  const [canManageRoles, setCanManageRoles] = useState(false);

  useEffect(() => {
    // Subscribe to roles
    const unsubscribe = subscribeToRoles(setRoles);
    setLoading(false);
    return unsubscribe;
  }, []);

  useEffect(() => {
    // Check if user can manage roles
    const checkAccess = () => {
      if (userProfile?.role) {
        // Check if user has manage_roles permission or is admin
        const hasManageRoles = userProfile.role.grantedPermissions.includes(PermissionId.MANAGE_ROLES);
        const isAdmin = userProfile.role.name.toLowerCase() === 'admin';
        setCanManageRoles(hasManageRoles || isAdmin);
      }
    };

    checkAccess();
  }, [userProfile]);

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setNewRole(role);
    } else {
      setEditingRole(null);
      setNewRole({
        name: '',
        description: '',
        grantedPermissions: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setNewRole({
      name: '',
      description: '',
      grantedPermissions: []
    });
  };

  const handleSaveRole = async () => {
    if (!canManageRoles) {
      setError('You do not have permission to manage roles');
      return;
    }

    setLoading(true);
    try {
      if (editingRole) {
        await updateRole(editingRole.id, {
          name: newRole.name || '',
          description: newRole.description || '',
          grantedPermissions: newRole.grantedPermissions || []
        });
      } else {
        await createRole({
          name: newRole.name || '',
          description: newRole.description || '',
          grantedPermissions: newRole.grantedPermissions || []
        });
      }
      handleCloseDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save role');
    }
    setLoading(false);
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!canManageRoles) {
      setError('You do not have permission to manage roles');
      return;
    }

    setLoading(true);
    try {
      await deleteRole(roleId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role');
    }
    setLoading(false);
  };

  const handlePermissionChange = (permissionId: PermissionId) => {
    setNewRole(prev => ({
      ...prev,
      grantedPermissions: prev.grantedPermissions?.includes(permissionId)
        ? prev.grantedPermissions.filter(p => p !== permissionId)
        : [...(prev.grantedPermissions || []), permissionId]
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Role Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!canManageRoles}
        >
          Add New Role
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Role Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map(role => (
              <TableRow key={role.id}>
                <TableCell>{role.name}</TableCell>
                <TableCell>{role.description}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {role.grantedPermissions.map(permissionId => {
                      const permission = getAllPermissions().find(p => p.id === permissionId);
                      return permission ? (
                        <Chip
                          key={permissionId}
                          label={permission.name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : null;
                    })}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleOpenDialog(role)}
                    color="primary"
                    disabled={!canManageRoles}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteRole(role.id)}
                    color="error"
                    disabled={!canManageRoles || role.name.toLowerCase() === 'admin'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Role Name"
              value={newRole.name}
              onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              value={newRole.description}
              onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Permissions
            </Typography>
            <Grid container spacing={2}>
              {getAllCategories().map(category => (
                <Grid key={category} item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {category}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getPermissionsByCategory(category).map(permission => (
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={newRole.grantedPermissions?.includes(permission.id as PermissionId)}
                            onChange={() => handlePermissionChange(permission.id as PermissionId)}
                          />
                        }
                        label={permission.name}
                      />
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveRole} variant="contained" color="primary">
            {editingRole ? 'Save Changes' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagementPage; 